import { LoadingDots } from '@carboncopyco/ui';
import Editor, {
  type BeforeMount,
  type EditorProps,
  loader,
  type OnMount,
  type Monaco,
} from '@monaco-editor/react';
import type * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import useOptions from './hooks/useOptions';
import { themes } from './theme';

// Fetch the Monaco Editor from CDN.
loader.config({
  paths: {
    vs: 'https://assets.demostack.com/vs',
  },
});

export type MonacoEditor = monaco.editor.IStandaloneCodeEditor;
export type MonacoEditorDecorations =
  monaco.editor.IEditorDecorationsCollection;

export interface CodeEditorProps
  extends Pick<
    EditorProps,
    'value' | 'language' | 'height' | 'options' | 'onMount' | 'onChange'
  > {
  onError?: EditorProps['onValidate'];
}

export const CodeEditor = ({
  language,
  onChange,
  onError,
  onMount,
  value,
  height = '328px',
  options = {},
}: CodeEditorProps) => {
  const editorOptions = useOptions(options);

  const handleBeforeMount: BeforeMount = (monaco) => {
    monaco.editor.defineTheme(themes.light.name, themes.light);
    monaco.editor.defineTheme(themes.readOnly.name, themes.readOnly);
  };

  const handleOnMount: OnMount = (editor, monaco) => {
    const handlers = [handleThemeChange];
    editor.onDidChangeConfiguration((event) =>
      handlers.forEach((handler) => handler(event, editor, monaco))
    );
    onMount?.(editor, monaco);
  };

  const handleThemeChange = (
    event: monaco.editor.ConfigurationChangedEvent,
    editor: monaco.editor.IStandaloneCodeEditor,
    monaco: Monaco
  ) => {
    if (event.hasChanged(monaco.editor.EditorOption.readOnly)) {
      const readOnly = editor.getOption(monaco.editor.EditorOption.readOnly);
      editor.updateOptions({
        theme: readOnly ? 'ds-read-only' : 'ds-light',
      });
    }
  };

  return (
    <Editor
      onChange={onChange}
      onValidate={onError}
      loading={<LoadingDots />}
      onMount={handleOnMount}
      beforeMount={handleBeforeMount}
      height={height}
      language={language}
      theme={editorOptions.readOnly ? 'ds-read-only' : 'ds-light'}
      options={editorOptions}
      value={value}
    />
  );
};
